import {
  Experiment,
  ExperimentDiagnosticsDailySpendSeries,
  ExperimentType,
  ExperimentsDailyDeliveryParams,
  ExperimentsDailyDeliveryRow,
  ExperimentsDailyDeliveryTargetRow,
} from "../../http/api-types";

export type VariantKeyType = "campaign" | "prediction_flight";

export type VariantDef = {
  key: string;
  keyType: VariantKeyType;
  keyId: number;
  variant: string;
  trafficAllocation: number;
};

type VariantDeliveryTotals = {
  revenue: number;
  delivery: number;
  targetSpend: number;
};

export type DailyDeliveryVariantMetrics = {
  variant: string;
  keyId: number;
  trafficAllocationPct: number;
  revenue: number;
  delivery: number;
  targetSpend: number;
  spendDelta: number;
  spendDeltaPct: number | null;
  expectedDelivery: number;
  actualSharePct: number;
  deltaSharePct: number;
};

export type DailyDeliveryDayMetrics = {
  date: string;
  totalActualSpend: number;
  totalTargetSpend: number;
  totalDelivery: number;
  variants: DailyDeliveryVariantMetrics[];
};

export function toDateOnlyUTC(d: Date): string {
  return d.toISOString().split("T")[0];
}

function toStartOfDayUTC(d: Date): Date {
  const date = new Date(d);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

function parseDate(value?: string): Date | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

export function buildLast7DaysWindow(referenceDate: Date = new Date()) {
  const endDateObj = new Date(referenceDate);
  const endDate = toDateOnlyUTC(endDateObj);
  const endDateTime = endDateObj.toISOString();

  const startDateObj = new Date(endDateObj);
  startDateObj.setUTCDate(endDateObj.getUTCDate() - 6);
  const startDate = toDateOnlyUTC(startDateObj);
  const startDateTime = toStartOfDayUTC(startDateObj).toISOString();

  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(startDateObj);
    d.setUTCDate(startDateObj.getUTCDate() + i);
    dates.push(toDateOnlyUTC(d));
  }

  return { startDate, endDate, startDateTime, endDateTime, dates };
}

export function resolveDailyDeliveryDateTimeRange(
  activeExperiments: Experiment[],
  defaultStartDateTime: string,
  defaultEndDateTime: string,
) {
  const defaultStart = new Date(defaultStartDateTime);
  const defaultEnd = new Date(defaultEndDateTime);

  let earliestStartWithinWindow: Date | null = null;
  let latestEndWithinWindow: Date | null = null;
  let hasStartOutsideWindow = false;
  let hasEndOutsideWindow = false;

  for (const experiment of activeExperiments) {
    const experimentStart = parseDate(experiment.start_date);
    if (!experimentStart || experimentStart <= defaultStart || experimentStart > defaultEnd) {
      hasStartOutsideWindow = true;
    } else if (!earliestStartWithinWindow || experimentStart < earliestStartWithinWindow) {
      earliestStartWithinWindow = experimentStart;
    }

    const experimentEnd = parseDate(experiment.end_date);
    if (!experimentEnd || experimentEnd >= defaultEnd || experimentEnd < defaultStart) {
      hasEndOutsideWindow = true;
    } else if (!latestEndWithinWindow || experimentEnd > latestEndWithinWindow) {
      latestEndWithinWindow = experimentEnd;
    }
  }

  const startDateTime =
    !hasStartOutsideWindow && earliestStartWithinWindow
      ? earliestStartWithinWindow.toISOString()
      : defaultStart.toISOString();

  const endDateTime =
    !hasEndOutsideWindow && latestEndWithinWindow ? latestEndWithinWindow.toISOString() : defaultEnd.toISOString();

  return { startDateTime, endDateTime };
}

export function getVariantDefs(experiment: Experiment): VariantDef[] {
  if (experiment.type === ExperimentType.Global) {
    return (experiment.prediction_flights || []).map(pf => ({
      key: `prediction_flight:${pf.prediction_flight_id}`,
      keyType: "prediction_flight",
      keyId: pf.prediction_flight_id,
      variant: pf.variant,
      trafficAllocation: pf.traffic_allocation,
    }));
  }

  return (experiment.campaigns || []).map(c => ({
    key: `campaign:${c.campaign_id}`,
    keyType: "campaign",
    keyId: c.campaign_id,
    variant: c.variant,
    trafficAllocation: c.traffic_allocation,
  }));
}

export function buildDailyDeliveryParamsForExperiment(
  experiment: Experiment,
  defaultStartDateTime: string,
  defaultEndDateTime: string,
): ExperimentsDailyDeliveryParams | null {
  const variants = getVariantDefs(experiment);
  const ids = variants
    .map(variant => variant.keyId)
    .filter(id => id > 0)
    .sort((a, b) => a - b);

  if (ids.length === 0) {
    return null;
  }

  const { startDateTime, endDateTime } = resolveDailyDeliveryDateTimeRange(
    [experiment],
    defaultStartDateTime,
    defaultEndDateTime,
  );

  if (experiment.type === ExperimentType.Global) {
    return {
      experiment_id: String(experiment.id),
      start_date: startDateTime,
      end_date: endDateTime,
      prediction_flight_ids: ids.join(","),
    };
  }

  return {
    experiment_id: String(experiment.id),
    start_date: startDateTime,
    end_date: endDateTime,
    campaign_ids: ids.join(","),
  };
}

export function buildCurrentDayDeliveryParamsForExperiment(
  experiment: Experiment,
  referenceTime: Date = new Date(),
): ExperimentsDailyDeliveryParams | null {
  const variants = getVariantDefs(experiment);
  const ids = variants
    .map(variant => variant.keyId)
    .filter(id => id > 0)
    .sort((a, b) => a - b);

  if (ids.length === 0) {
    return null;
  }

  const windowEnd = new Date(referenceTime);
  const windowStart = toStartOfDayUTC(referenceTime);
  const experimentStart = parseDate(experiment.start_date) || parseDate(experiment.created_at);
  const experimentEnd = parseDate(experiment.end_date);

  if (experimentStart && experimentStart >= windowEnd) {
    return null;
  }
  if (experimentEnd && experimentEnd <= windowStart) {
    return null;
  }

  const startDateTime =
    experimentStart && experimentStart > windowStart ? experimentStart.toISOString() : windowStart.toISOString();
  const endDateTime =
    experimentEnd && experimentEnd < windowEnd ? experimentEnd.toISOString() : windowEnd.toISOString();

  if (new Date(startDateTime) >= new Date(endDateTime)) {
    return null;
  }

  if (experiment.type === ExperimentType.Global) {
    return {
      experiment_id: String(experiment.id),
      start_date: startDateTime,
      end_date: endDateTime,
      prediction_flight_ids: ids.join(","),
    };
  }

  return {
    experiment_id: String(experiment.id),
    start_date: startDateTime,
    end_date: endDateTime,
    campaign_ids: ids.join(","),
  };
}

export function buildDailyDeliveryByExperiment(
  activeExperiments: Experiment[],
  rows: ExperimentsDailyDeliveryRow[],
  targets: ExperimentsDailyDeliveryTargetRow[],
  last7Dates: string[],
): Record<number, DailyDeliveryDayMetrics[]> {
  const rowCampaignIndex = new Map<number, ExperimentsDailyDeliveryRow[]>();
  const rowPredictionFlightIndex = new Map<number, ExperimentsDailyDeliveryRow[]>();
  const targetCampaignIndex = new Map<number, ExperimentsDailyDeliveryTargetRow[]>();
  const targetPredictionFlightIndex = new Map<number, ExperimentsDailyDeliveryTargetRow[]>();

  for (const row of rows) {
    const campaignId = row.campaign_id || 0;
    if (campaignId > 0) {
      const existing = rowCampaignIndex.get(campaignId) || [];
      existing.push(row);
      rowCampaignIndex.set(campaignId, existing);
    }

    const predictionFlightId = row.prediction_flight_id || 0;
    if (predictionFlightId > 0) {
      const existing = rowPredictionFlightIndex.get(predictionFlightId) || [];
      existing.push(row);
      rowPredictionFlightIndex.set(predictionFlightId, existing);
    }
  }

  for (const target of targets) {
    const campaignId = target.campaign_id || 0;
    if (campaignId > 0) {
      const existing = targetCampaignIndex.get(campaignId) || [];
      existing.push(target);
      targetCampaignIndex.set(campaignId, existing);
    }

    const predictionFlightId = target.prediction_flight_id || 0;
    if (predictionFlightId > 0) {
      const existing = targetPredictionFlightIndex.get(predictionFlightId) || [];
      existing.push(target);
      targetPredictionFlightIndex.set(predictionFlightId, existing);
    }
  }

  const byExperiment: Record<number, DailyDeliveryDayMetrics[]> = {};

  for (const experiment of activeExperiments) {
    const variants = getVariantDefs(experiment);
    if (variants.length === 0) continue;

    const dayBuckets = new Map<string, Map<string, VariantDeliveryTotals>>();
    for (const date of last7Dates) {
      const variantBuckets = new Map<string, VariantDeliveryTotals>();
      for (const variant of variants) {
        variantBuckets.set(variant.key, { revenue: 0, delivery: 0, targetSpend: 0 });
      }
      dayBuckets.set(date, variantBuckets);
    }

    for (const variant of variants) {
      const candidateRows =
        variant.keyType === "campaign"
          ? (rowCampaignIndex.get(variant.keyId) ?? [])
          : (rowPredictionFlightIndex.get(variant.keyId) ?? []);

      for (const row of candidateRows) {
        const date = row.date;
        if (!dayBuckets.has(date)) continue;

        const variantsForDay = dayBuckets.get(date);
        if (!variantsForDay) continue;
        const current = variantsForDay.get(variant.key) || { revenue: 0, delivery: 0, targetSpend: 0 };
        const revenue = Number(row.revenue || 0);
        const delivery = Number(row.delivery || revenue);

        variantsForDay.set(variant.key, {
          revenue: current.revenue + revenue,
          delivery: current.delivery + delivery,
          targetSpend: current.targetSpend,
        });
      }

      const candidateTargets =
        variant.keyType === "campaign"
          ? (targetCampaignIndex.get(variant.keyId) ?? [])
          : (targetPredictionFlightIndex.get(variant.keyId) ?? []);

      for (const target of candidateTargets) {
        const date = target.date;
        if (!dayBuckets.has(date)) continue;

        const variantsForDay = dayBuckets.get(date);
        if (!variantsForDay) continue;
        const current = variantsForDay.get(variant.key) || { revenue: 0, delivery: 0, targetSpend: 0 };

        variantsForDay.set(variant.key, {
          revenue: current.revenue,
          delivery: current.delivery,
          targetSpend: current.targetSpend + Number(target.target_spend || 0),
        });
      }
    }

    byExperiment[experiment.id] = last7Dates.map(date => {
      const variantsForDay = dayBuckets.get(date) || new Map<string, VariantDeliveryTotals>();
      const totalActualSpend = variants.reduce(
        (sum, variant) => sum + (variantsForDay.get(variant.key)?.revenue || 0),
        0,
      );
      const totalTargetSpend = variants.reduce(
        (sum, variant) => sum + (variantsForDay.get(variant.key)?.targetSpend || 0),
        0,
      );
      const totalDelivery = variants.reduce(
        (sum, variant) => sum + (variantsForDay.get(variant.key)?.delivery || 0),
        0,
      );

      return {
        date,
        totalActualSpend,
        totalTargetSpend,
        totalDelivery,
        variants: variants.map(variant => {
          const totals = variantsForDay.get(variant.key) || { revenue: 0, delivery: 0, targetSpend: 0 };
          const trafficAllocationPct = variant.trafficAllocation / 100;
          const expectedDelivery = (totalDelivery * variant.trafficAllocation) / 10000;
          const actualSharePct = totalActualSpend > 0 ? (totals.revenue / totalActualSpend) * 100 : 0;
          const deltaSharePct = actualSharePct - trafficAllocationPct;
          const spendDelta = totals.revenue - totals.targetSpend;
          const spendDeltaPct = totals.targetSpend > 0 ? (spendDelta / totals.targetSpend) * 100 : null;

          return {
            variant: variant.variant,
            keyId: variant.keyId,
            trafficAllocationPct,
            revenue: totals.revenue,
            delivery: totals.delivery,
            targetSpend: totals.targetSpend,
            spendDelta,
            spendDeltaPct,
            expectedDelivery,
            actualSharePct,
            deltaSharePct,
          };
        }),
      };
    });
  }

  return byExperiment;
}

export function buildDailyDeliveryMetricsForExperiment(
  experiment: Experiment,
  rows: ExperimentsDailyDeliveryRow[],
  targets: ExperimentsDailyDeliveryTargetRow[],
  last7Dates: string[],
): DailyDeliveryDayMetrics[] {
  return buildDailyDeliveryByExperiment([experiment], rows, targets, last7Dates)[experiment.id] || [];
}

export function buildDiagnosticsDailySpendMetrics(
  experiment: Experiment,
  dailySpendSeries: ExperimentDiagnosticsDailySpendSeries[],
  maxDays = 14,
): DailyDeliveryDayMetrics[] {
  const variants = getVariantDefs(experiment);
  if (variants.length === 0 || dailySpendSeries.length === 0) {
    return [];
  }

  const allDates = Array.from(
    new Set(
      dailySpendSeries.flatMap(series =>
        series.points.map(point => {
          return point.date.split("T")[0];
        }),
      ),
    ),
  )
    .sort()
    .slice(-maxDays);

  if (allDates.length === 0) {
    return [];
  }

  const pointIndex = new Map<string, Map<string, { actualSpend: number; targetSpend: number }>>();
  for (const series of dailySpendSeries) {
    const seriesKey = `${series.member_type}:${series.member_id}`;
    const pointsByDate = new Map<string, { actualSpend: number; targetSpend: number }>();

    for (const point of series.points) {
      const date = point.date.split("T")[0];
      pointsByDate.set(date, {
        actualSpend: Number(point.actual_spend || 0),
        targetSpend: Number(point.target_spend || 0),
      });
    }

    pointIndex.set(seriesKey, pointsByDate);
  }

  return allDates.map(date => {
    const totalsByVariant = variants.map(variant => {
      const seriesKey = `${variant.keyType}:${variant.keyId}`;
      const point = pointIndex.get(seriesKey)?.get(date);
      return {
        variant,
        revenue: point?.actualSpend || 0,
        delivery: point?.actualSpend || 0,
        targetSpend: point?.targetSpend || 0,
      };
    });

    const totalActualSpend = totalsByVariant.reduce((sum, item) => sum + item.revenue, 0);
    const totalTargetSpend = totalsByVariant.reduce((sum, item) => sum + item.targetSpend, 0);
    const totalDelivery = totalsByVariant.reduce((sum, item) => sum + item.delivery, 0);

    return {
      date,
      totalActualSpend,
      totalTargetSpend,
      totalDelivery,
      variants: totalsByVariant.map(({ variant, revenue, delivery, targetSpend }) => {
        const trafficAllocationPct = variant.trafficAllocation / 100;
        const expectedDelivery = (totalDelivery * variant.trafficAllocation) / 10000;
        const actualSharePct = totalActualSpend > 0 ? (revenue / totalActualSpend) * 100 : 0;
        const deltaSharePct = actualSharePct - trafficAllocationPct;
        const spendDelta = revenue - targetSpend;
        const spendDeltaPct = targetSpend > 0 ? (spendDelta / targetSpend) * 100 : null;

        return {
          variant: variant.variant,
          keyId: variant.keyId,
          trafficAllocationPct,
          revenue,
          delivery,
          targetSpend,
          spendDelta,
          spendDeltaPct,
          expectedDelivery,
          actualSharePct,
          deltaSharePct,
        };
      }),
    };
  });
}

export function mergeDailyDeliveryDays(
  historyDays: DailyDeliveryDayMetrics[],
  liveDays: DailyDeliveryDayMetrics[],
  maxDays = 14,
): DailyDeliveryDayMetrics[] {
  if (liveDays.length === 0) {
    return historyDays.slice(-maxDays);
  }

  const mergedByDate = new Map<string, DailyDeliveryDayMetrics>();
  for (const day of historyDays) {
    mergedByDate.set(day.date, day);
  }
  for (const day of liveDays) {
    mergedByDate.set(day.date, day);
  }

  return Array.from(mergedByDate.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-maxDays);
}
